import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import style from "./FirstPersonAvatarPreview.scss";
import { fetchAvatar } from "../../utils/avatar-utils";
import cartoonAvatarImage from "../../assets/images/avatar-previews/cartoon_avatar.png";
import DashboardChannel from "../../utils/dashboard-channel";
import { AvatarPreviewPopover } from "./AvatarPreviewPopover";
import { Button, Spinner } from "@fluentui/react-components";
import { useModalContext } from "./ModalContext";
import { EditNameModal } from "./EditNameModal";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { getAuthUserSubscriptionsAndLimits } from "../../utils/get-auth-user";
import configs from "../../utils/configs";

export function FirstPersonAvatarPreview({
  signedIn,
  profile,
  isAbsolute,
  showContextualSignInDialog,
  onEditAvatar,
  onSignOut,
  store
}) {
  const [avatar, setAvatar] = useState(store.state.profile || null);
  const intl = useIntl();

  const storeAvatarInDashboard = useCallback(
    async avatar => {
      const isAuth = store.state.authUser && store.state.authUser.id;

      if (avatar && avatar.avatar_id && isAuth) {
        const dashboardChannel = new DashboardChannel(store, store.state.hubId);
        dashboardChannel.storeAvatar(avatar);
      }

      store.update({
        profile: { ...store.profile, thumbnail: avatar.files.thumbnail }
      });
    },
    [store]
  );

  const refetchAvatar = useCallback(
    async avatarId => {
      if (!avatarId) {
        console.error("avatarId is null");
        return;
      }

      const avatar = await fetchAvatar(avatarId);

      storeAvatarInDashboard(avatar);

      if (!avatar || avatarId !== avatar.avatar_id) {
        return; // Avatar was deleted or changed while we were waiting for the fetch to complete.
      }

      setAvatar({
        avatarId,
        thumbnail: avatar.files.thumbnail
      });
    },
    [storeAvatarInDashboard]
  );

  useEffect(() => {
    refetchAvatar(profile.avatarId);
  }, [profile.avatarId, refetchAvatar]);

  const { showNonHistoriedDialog, closeDialog } = useModalContext();

  const handleSaveName = displayName => {
    store.update({
      profile: {
        ...profile,
        displayName
      }
    });

    notificationsCenter.success(
      intl.formatMessage({
        id: "room-settings-sidebar.name-saved",
        defaultMessage: "Name updated successfully"
      })
    );
  };

  const handleEditName = () => {
    showNonHistoriedDialog(EditNameModal, {
      profile,
      onSave: handleSaveName,
      onClose: closeDialog
    });
  };

  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    window.APP.scene.addEventListener("fetching_auth_user", () => {
      setIsFetching(true);
    });
    window.APP.scene.addEventListener("fetching_auth_user_end", () => {
      setIsFetching(false);
    });

    return () => {
      window.APP.scene.removeEventListener("fetching_auth_user", () => setIsFetching(true));
      window.APP.scene.removeEventListener("fetching_auth_user_end", () => setIsFetching(false));
    };
  }, []);

  useEffect(() => {
    if (signedIn) {
      setIsFetching(false);
    }
  }, [signedIn]);

  const [open, setOpen] = useState(false);

  // We have the logic to manage multiple subscriptions
  // but for now subscriptions will be an array of length 1
  const [subscriptions, setSubscriptions] = useState(null);
  const subscriptionsEnabled = configs.ENABLE_SUBSCRIPTIONS === "true" || configs.ENABLE_SUBSCRIPTIONS === true;
  const subscriptionName = useMemo(() => subscriptions?.find(s => s.name)?.name || "Standard", [subscriptions]);

  const scenesLimit = useMemo(() => subscriptions?.find(s => s.maxScenes)?.maxScenes || 3, [subscriptions]);
  const storageLimit = useMemo(() => subscriptions?.find(s => s.maxStorage)?.maxStorage || 512, [subscriptions]);
  const ccuLimit = useMemo(() => subscriptions?.find(s => s.maxCcu)?.maxCcu || 5, [subscriptions]);

  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [scenesUsed, setScenesUsed] = useState(0);
  const [storageUsed, setStorageUsed] = useState(0);

  useEffect(() => {
    if (signedIn && open && subscriptionsEnabled) {
      setLoadingSubscriptions(true);
      getAuthUserSubscriptionsAndLimits()
        .then(subscription => {
          setSubscriptions([subscription]);
          setScenesUsed(subscription.usedScenes);
          setStorageUsed(subscription.usedStorage);
        })
        .finally(() => {
          setLoadingSubscriptions(false);
        });
    }
  }, [signedIn, open, subscriptionsEnabled]);

  return (
    <div className={classNames(style.firstPersonAvatarPreview, { [style.absolute]: isAbsolute })}>
      {signedIn && avatar && (
        <AvatarPreviewPopover
          image={avatar?.thumbnail || cartoonAvatarImage}
          name={profile.displayName}
          onEditAvatar={onEditAvatar}
          onSignOut={onSignOut}
          onEditName={handleEditName}
          open={open}
          setOpen={setOpen}
          storageLimit={storageLimit}
          storageUsed={storageUsed}
          ccuLimit={ccuLimit}
          ccuUsed={0}
          scenesLimit={scenesLimit}
          scenesUsed={scenesUsed}
          loadingSubscriptions={loadingSubscriptions}
          subscriptionName={subscriptionName}
          subscriptionsEnabled={subscriptionsEnabled}
        />
      )}
      {!signedIn && !isFetching && (
        <Button appearance="primary" size="small" onClick={showContextualSignInDialog}>
          <FormattedMessage id="sign-in-button" defaultMessage="Sign in" />
        </Button>
      )}
      {!signedIn && isFetching && <Spinner size="large" appearance="primary" />}
    </div>
  );
}

FirstPersonAvatarPreview.propTypes = {
  signedIn: PropTypes.bool,
  avatarId: PropTypes.string,
  profile: PropTypes.object,
  customStyle: PropTypes.string,
  isAbsolute: PropTypes.bool,
  onClick: PropTypes.func,
  showContextualSignInDialog: PropTypes.func,
  onEditAvatar: PropTypes.func,
  onSignOut: PropTypes.func,
  store: PropTypes.object
};
